import {
  addDays,
  differenceInCalendarDays,
  isBefore,
  isSameDay,
  setHours,
  setMinutes,
  startOfDay,
} from 'date-fns';

export type TouchpointChannel = 'call' | 'sms' | 'email' | 'whatsapp';

export type CadenceSlot = 'am' | 'pm';

export type CadenceStep = {
  day: number;
  am?: TouchpointChannel[];
  pm?: TouchpointChannel[];
};

export type LeadCadenceState = {
  version: 1;
  startedAt: string;
  paused: boolean;
  pausedAt?: string | null;
  completedKeys: string[];
  touchpointLogs?: TouchpointLogEntry[];
};

export type TouchpointLogEntry = {
  key: string;
  day: number;
  slot: CadenceSlot;
  channel: TouchpointChannel;
  status: 'completed' | 'skipped';
  skipReason?: string | null;
  skipNotes?: string | null;
  callOutcome?: CallOutcome | null;
};

export const CADENCE_SKIP_REASONS = [
  { value: 'asked_to_call_back', label: 'Lead asked to call back later' },
  { value: 'invalid_contact', label: 'Invalid or wrong contact details' },
  { value: 'not_interested_now', label: 'Not interested right now' },
  { value: 'already_booked_elsewhere', label: 'Already booked elsewhere' },
  { value: 'duplicate', label: 'Duplicate lead' },
  { value: 'other', label: 'Other' },
] as const;

export const CALL_OUTCOMES = [
  { value: 'no_answer', label: 'No answer' },
  { value: 'voicemail', label: 'Left voicemail' },
  { value: 'spoke', label: 'Spoke to lead' },
  { value: 'wrong_number', label: 'Wrong number' },
] as const;

export type CallOutcome = (typeof CALL_OUTCOMES)[number]['value'];

export type CadenceSkipReason = (typeof CADENCE_SKIP_REASONS)[number]['value'];

export type FollowUpCadenceTier = 'fresh' | 'warming' | 'stale' | 'critical';

export type CadenceTouchpoint = {
  day: number;
  slot: CadenceSlot;
  channel: TouchpointChannel;
  key: string;
};

export type CadenceBoardSummary = {
  label: string;
  overdue: boolean;
  dueCount: number;
  dayNumber: number;
};

export const CADENCE_SLOT_TIMES: Record<
  CadenceSlot,
  { hour: number; minute: number }
> = {
  am: { hour: 9, minute: 30 },
  pm: { hour: 17, minute: 30 },
};

export const CADENCE_SLOT_LABELS: Record<CadenceSlot, string> = {
  am: '9:30 AM',
  pm: '5:30 PM',
};

export const CADENCE_SLOT_OPTIONS: Array<{
  value: '' | CadenceSlot;
  label: string;
}> = [
  { value: '', label: 'Auto (from cadence schedule)' },
  { value: 'am', label: `Morning (${CADENCE_SLOT_LABELS.am})` },
  { value: 'pm', label: `Evening (${CADENCE_SLOT_LABELS.pm})` },
];

export function getCadenceSlotShortLabel(slot: CadenceSlot | null | undefined) {
  if (slot === 'am') {
    return `AM (${CADENCE_SLOT_LABELS.am})`;
  }

  if (slot === 'pm') {
    return `PM (${CADENCE_SLOT_LABELS.pm})`;
  }

  return 'Auto';
}

export function preferCadenceSlotSteps<T>(
  steps: T[],
  runtimeSlot: CadenceSlot,
  getSlot: (step: T) => CadenceSlot | string | null | undefined = (step) =>
    (step as { cadence_slot?: CadenceSlot | string | null }).cadence_slot,
): T[] {
  const specific = steps.filter((step) => getSlot(step) === runtimeSlot);

  if (specific.length > 0) {
    return specific;
  }

  return steps.filter((step) => !getSlot(step));
}

export function resolveRuntimeCadenceSlot(input: {
  slot?: CadenceSlot | null;
  cadenceDay?: number;
  channel?: TouchpointChannel | null;
}): CadenceSlot {
  if (input.slot === 'am' || input.slot === 'pm') {
    return input.slot;
  }

  if (input.cadenceDay && input.channel) {
    return inferSlotForDayChannel(input.cadenceDay, input.channel);
  }

  return 'am';
}

export const LEAD_CADENCE_SCHEDULE: CadenceStep[] = [
  { day: 1, am: ['call'], pm: ['sms', 'email'] },
  { day: 2, am: ['call'], pm: ['whatsapp'] },
  { day: 5, am: ['call'] },
  { day: 10, am: ['email'] },
  { day: 14, am: ['call'] },
  { day: 21, am: ['sms'] },
  { day: 30, am: ['call'] },
  { day: 60, am: ['email'] },
  { day: 90, am: ['call'] },
];

export const CADENCE_FINAL_DAY = 90;

const CHANNEL_LABELS: Record<TouchpointChannel, string> = {
  call: 'Call',
  sms: 'Text',
  email: 'Email',
  whatsapp: 'WhatsApp',
};

const TERMINAL_STATUSES = new Set(['CLOSED', 'BOOKED']);

/** Every non-terminal pipeline lead enters the 90-day cadence — including payment plans, scheduled callbacks, and finance applications. */
export const CADENCE_INCLUDES_ALL_ACTIVE_LEADS = true;

export const CADENCE_TERMINAL_STATUSES = ['CLOSED', 'BOOKED'] as const;

type LeadLike = {
  status?: string | null;
  due?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt?: string | null;
  created_at?: string | null;
  cadence_state?: DbCadenceStateRecord | null;
};

type DbCadenceStateRecord = {
  id?: string | null;
  started_at?: string | null;
  paused?: boolean | null;
  paused_at?: string | null;
  version?: number | null;
  touchpoint_logs?: Array<{
    day: number;
    channel: string;
    status: string;
    skip_reason?: string | null;
    skip_notes?: string | null;
    call_outcome?: string | null;
    slot?: string | null;
    created_at?: string | null;
  }> | null;
};

type ContactLike = {
  type?: string | null;
  createdAt?: string | null;
  created_at?: string | null;
};

export function touchpointKey(
  day: number,
  slot: CadenceSlot,
  channel: TouchpointChannel,
) {
  return `${day}:${slot}:${channel}`;
}

export function inferSlotForDayChannel(
  day: number,
  channel: TouchpointChannel,
): CadenceSlot {
  const step = LEAD_CADENCE_SCHEDULE.find((entry) => entry.day === day);

  if (!step) {
    return 'am';
  }

  if (step.am?.includes(channel)) {
    return 'am';
  }

  if (step.pm?.includes(channel)) {
    return 'pm';
  }

  return 'am';
}

export function normalizeTouchpointKey(key: string) {
  const parts = key.split(':');

  if (parts.length === 3) {
    const [dayRaw, slot, channel] = parts;
    const day = Number(dayRaw);

    if (day && (slot === 'am' || slot === 'pm') && channel) {
      return touchpointKey(day, slot, channel as TouchpointChannel);
    }
  }

  if (parts.length === 2) {
    const [dayRaw, channel] = parts;
    const day = Number(dayRaw);

    if (day && channel) {
      return touchpointKey(
        day,
        inferSlotForDayChannel(day, channel as TouchpointChannel),
        channel as TouchpointChannel,
      );
    }
  }

  return key;
}

export function normalizeCompletedKeys(keys: string[]) {
  return Array.from(new Set(keys.map((key) => normalizeTouchpointKey(key))));
}

export function isTouchpointKeyCompleted(
  state: Pick<LeadCadenceState, 'completedKeys'>,
  key: string,
) {
  const normalized = normalizeTouchpointKey(key);
  const normalizedKeys = normalizeCompletedKeys(state.completedKeys);

  return normalizedKeys.includes(normalized);
}

export function getCadenceSlotLabel(slot: CadenceSlot) {
  return CADENCE_SLOT_LABELS[slot];
}

export function getCallOutcomeLabel(outcome: CallOutcome | null | undefined) {
  if (!outcome) {
    return null;
  }

  return (
    CALL_OUTCOMES.find((entry) => entry.value === outcome)?.label || outcome
  );
}

export function dayHasCallTouchpoint(day: number) {
  return listCadenceTouchpoints().some(
    (touchpoint) => touchpoint.day === day && touchpoint.channel === 'call',
  );
}

export function isDayCallResolved(state: LeadCadenceState, day: number) {
  if (!dayHasCallTouchpoint(day)) {
    return true;
  }

  const callTouchpoints = listCadenceTouchpoints().filter(
    (touchpoint) => touchpoint.day === day && touchpoint.channel === 'call',
  );

  if (
    callTouchpoints.some((touchpoint) =>
      isTouchpointKeyCompleted(state, touchpoint.key),
    )
  ) {
    return true;
  }

  const callLog = state.touchpointLogs?.find(
    (log) => log.day === day && log.channel === 'call',
  );

  return callLog?.status === 'skipped';
}

export function getCallOutcomeForDay(
  state: LeadCadenceState,
  day: number,
): CallOutcome | null {
  const callLog = state.touchpointLogs?.find(
    (log) =>
      log.day === day &&
      log.channel === 'call' &&
      log.status === 'completed' &&
      log.callOutcome,
  );

  return (callLog?.callOutcome as CallOutcome | undefined) || null;
}

export function getTouchpointLabel(channel: TouchpointChannel) {
  return CHANNEL_LABELS[channel];
}

export function getCadenceFromMetadata(
  metadata?: Record<string, unknown> | null,
): LeadCadenceState | null {
  const raw = metadata?.cadence;
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const cadence = raw as Partial<LeadCadenceState>;
  if (!cadence.startedAt || cadence.version !== 1) {
    return null;
  }

  return {
    version: 1,
    startedAt: cadence.startedAt,
    paused: Boolean(cadence.paused),
    pausedAt: cadence.pausedAt || null,
    completedKeys: normalizeCompletedKeys(
      Array.isArray(cadence.completedKeys)
        ? cadence.completedKeys.filter((key) => typeof key === 'string')
        : [],
    ),
    touchpointLogs: Array.isArray(cadence.touchpointLogs)
      ? (cadence.touchpointLogs as TouchpointLogEntry[])
      : [],
  };
}

export function stateFromDbRecord(
  record: DbCadenceStateRecord,
): LeadCadenceState | null {
  if (!record.started_at) {
    return null;
  }

  const startedAtMs = new Date(record.started_at).getTime();

  const touchpointLogs: TouchpointLogEntry[] = (record.touchpoint_logs || [])
    .filter(
      (log) =>
        typeof log.day === 'number' &&
        typeof log.channel === 'string' &&
        (log.status === 'completed' || log.status === 'skipped'),
    )
    .filter((log) => {
      if (!log.created_at) {
        return true;
      }

      return new Date(log.created_at).getTime() >= startedAtMs;
    })
    .map((log) => {
      const channel = log.channel as TouchpointChannel;
      const slot = (
        log.slot === 'pm' ? 'pm' : inferSlotForDayChannel(log.day, channel)
      ) as CadenceSlot;

      return {
        key: touchpointKey(log.day, slot, channel),
        day: log.day,
        slot,
        channel,
        status: log.status as 'completed' | 'skipped',
        skipReason: log.skip_reason,
        skipNotes: log.skip_notes,
        callOutcome: (log.call_outcome as CallOutcome | null) || null,
      };
    });

  return {
    version: 1,
    startedAt: record.started_at,
    paused: Boolean(record.paused),
    pausedAt: record.paused_at || null,
    completedKeys: normalizeCompletedKeys(touchpointLogs.map((log) => log.key)),
    touchpointLogs,
  };
}

export function resolveLeadCadenceState(
  lead: LeadLike,
): LeadCadenceState | null {
  if (!isCadenceEligible(lead)) {
    return null;
  }

  if (lead.cadence_state?.started_at) {
    return stateFromDbRecord(lead.cadence_state);
  }

  return (
    getCadenceFromMetadata(lead.metadata) ||
    buildInitialCadenceState(getLeadCreatedAt(lead))
  );
}

export function hasPersistedCadence(lead: LeadLike) {
  return Boolean(
    lead.cadence_state?.started_at || getCadenceFromMetadata(lead.metadata),
  );
}

export function isCadenceEligible(lead: LeadLike) {
  if (!CADENCE_INCLUDES_ALL_ACTIVE_LEADS) {
    return false;
  }

  const status = lead.status || 'NEW';
  return !TERMINAL_STATUSES.has(status);
}

export function needsCadenceInitialization(lead: LeadLike) {
  if (!isCadenceEligible(lead)) {
    return false;
  }

  return !hasPersistedCadence(lead);
}

export function isLeadCadenceWorkDue(lead: LeadLike, now: Date = new Date()) {
  if (!isCadenceEligible(lead)) {
    return false;
  }

  if (resolveLeadCadenceState(lead)?.paused) {
    return false;
  }

  const state = resolveLeadCadenceState(lead);
  if (!state) {
    return false;
  }

  return getDueTouchpoints(state, now).length > 0;
}

export function compareLeadCadenceWorkPriority(
  a: LeadLike,
  b: LeadLike,
  now: Date = new Date(),
) {
  const aOverdue = isCadenceOverdue(a, now) ? 1 : 0;
  const bOverdue = isCadenceOverdue(b, now) ? 1 : 0;

  if (aOverdue !== bOverdue) {
    return bOverdue - aOverdue;
  }

  const aSummary = getCadenceBoardSummary(a, now);
  const bSummary = getCadenceBoardSummary(b, now);

  return (bSummary?.dueCount || 0) - (aSummary?.dueCount || 0);
}

export function getLeadCreatedAt(lead: LeadLike) {
  const raw = lead.createdAt || lead.created_at;
  return raw ? new Date(raw) : new Date();
}

export function buildInitialCadenceState(
  startedAt: Date = new Date(),
): LeadCadenceState {
  return {
    version: 1,
    startedAt: startedAt.toISOString(),
    paused: false,
    pausedAt: null,
    completedKeys: [],
    touchpointLogs: [],
  };
}

export function buildRestartedCadenceState(
  startedAt: Date = new Date(),
): LeadCadenceState {
  return buildInitialCadenceState(startedAt);
}

export function listCadenceTouchpoints(): CadenceTouchpoint[] {
  return LEAD_CADENCE_SCHEDULE.flatMap((step) => {
    const entries: CadenceTouchpoint[] = [];

    for (const channel of step.am || []) {
      entries.push({
        day: step.day,
        slot: 'am',
        channel,
        key: touchpointKey(step.day, 'am', channel),
      });
    }

    for (const channel of step.pm || []) {
      entries.push({
        day: step.day,
        slot: 'pm',
        channel,
        key: touchpointKey(step.day, 'pm', channel),
      });
    }

    return entries;
  });
}

export function getCadenceDayNumber(
  state: LeadCadenceState,
  now: Date = new Date(),
) {
  const startedAt = new Date(state.startedAt);
  return Math.max(
    1,
    differenceInCalendarDays(startOfDay(now), startOfDay(startedAt)) + 1,
  );
}

export function getTouchpointDueDate(
  startedAt: Date,
  day: number,
  slot: CadenceSlot = 'am',
) {
  const { hour, minute } = CADENCE_SLOT_TIMES[slot];
  return setMinutes(
    setHours(addDays(startOfDay(startedAt), day - 1), hour),
    minute,
  );
}

export function getNextIncompleteTouchpoint(
  state: LeadCadenceState,
): CadenceTouchpoint | null {
  for (const touchpoint of listCadenceTouchpoints()) {
    if (!isTouchpointKeyCompleted(state, touchpoint.key)) {
      return touchpoint;
    }
  }

  return null;
}

export function getNextCadenceDueDate(
  state: LeadCadenceState,
  now: Date = new Date(),
) {
  const next = getNextIncompleteTouchpoint(state);
  if (!next) {
    return null;
  }

  const startedAt = new Date(state.startedAt);
  const scheduled = getTouchpointDueDate(startedAt, next.day, next.slot);

  if (isBefore(scheduled, now)) {
    return scheduled;
  }

  return scheduled;
}

export function getDueTouchpoints(
  state: LeadCadenceState,
  now: Date = new Date(),
): CadenceTouchpoint[] {
  const startedAt = new Date(state.startedAt);

  return listCadenceTouchpoints().filter((touchpoint) => {
    if (isTouchpointKeyCompleted(state, touchpoint.key)) {
      return false;
    }

    const dueAt = getTouchpointDueDate(
      startedAt,
      touchpoint.day,
      touchpoint.slot,
    );
    if (isBefore(now, dueAt)) {
      return false;
    }

    if (
      touchpoint.channel !== 'call' &&
      !isDayCallResolved(state, touchpoint.day)
    ) {
      return false;
    }

    return true;
  });
}

export function isCadenceComplete(state: LeadCadenceState) {
  return getNextIncompleteTouchpoint(state) === null;
}

export function shouldSuggestClose(state: LeadCadenceState) {
  return isCadenceComplete(state);
}

export function markTouchpointComplete(
  state: LeadCadenceState,
  day: number,
  channel: TouchpointChannel,
  callOutcome?: CallOutcome | null,
  slot?: CadenceSlot,
): LeadCadenceState {
  const resolvedSlot = slot || inferSlotForDayChannel(day, channel);
  const key = touchpointKey(day, resolvedSlot, channel);

  if (isTouchpointKeyCompleted(state, key)) {
    return state;
  }

  const touchpointLogs = [...(state.touchpointLogs || [])];
  touchpointLogs.push({
    key,
    day,
    slot: resolvedSlot,
    channel,
    status: 'completed',
    callOutcome: channel === 'call' ? callOutcome || null : null,
  });

  return {
    ...state,
    completedKeys: normalizeCompletedKeys([...state.completedKeys, key]),
    touchpointLogs,
  };
}

export function markTouchpointSkipped(
  state: LeadCadenceState,
  day: number,
  channel: TouchpointChannel,
  skipReason: string,
  skipNotes?: string | null,
  slot?: CadenceSlot,
): LeadCadenceState {
  const resolvedSlot = slot || inferSlotForDayChannel(day, channel);
  const key = touchpointKey(day, resolvedSlot, channel);

  if (isTouchpointKeyCompleted(state, key)) {
    return state;
  }

  const touchpointLogs = [...(state.touchpointLogs || [])];
  touchpointLogs.push({
    key,
    day,
    slot: resolvedSlot,
    channel,
    status: 'skipped',
    skipReason,
    skipNotes,
  });

  return {
    ...state,
    completedKeys: normalizeCompletedKeys([...state.completedKeys, key]),
    touchpointLogs,
  };
}

export function mapContactTypeToChannel(
  type?: string | null,
): TouchpointChannel | null {
  if (!type) {
    return null;
  }

  const normalized = type.toLowerCase();
  if (normalized === 'call') {
    return 'call';
  }
  if (normalized === 'sms' || normalized === 'text') {
    return 'sms';
  }
  if (normalized === 'email') {
    return 'email';
  }
  if (normalized === 'whatsapp') {
    return 'whatsapp';
  }

  return null;
}

export function syncCadenceWithContacts(
  state: LeadCadenceState,
  contacts: ContactLike[],
): LeadCadenceState {
  const startedAt = new Date(state.startedAt);
  let nextState = state;

  const sortedContacts = [...contacts].sort((a, b) => {
    const aDate = new Date(a.createdAt || a.created_at || 0).getTime();
    const bDate = new Date(b.createdAt || b.created_at || 0).getTime();
    return aDate - bDate;
  });

  for (const contact of sortedContacts) {
    const createdAt = contact.createdAt || contact.created_at;
    if (!createdAt) {
      continue;
    }

    const contactDate = new Date(createdAt);
    if (isBefore(contactDate, startedAt)) {
      continue;
    }

    const channel = mapContactTypeToChannel(contact.type);
    if (!channel) {
      continue;
    }

    const dayAtContact = Math.max(
      1,
      differenceInCalendarDays(startOfDay(contactDate), startOfDay(startedAt)) +
        1,
    );

    const match = listCadenceTouchpoints().find(
      (touchpoint) =>
        touchpoint.channel === channel &&
        touchpoint.day <= dayAtContact &&
        !isTouchpointKeyCompleted(nextState, touchpoint.key),
    );

    if (match) {
      nextState = markTouchpointComplete(
        nextState,
        match.day,
        match.channel,
        null,
        match.slot,
      );
    }
  }

  return nextState;
}

export function mergeCadenceIntoMetadata(
  metadata: Record<string, unknown> | null | undefined,
  state: LeadCadenceState,
) {
  return {
    ...(metadata || {}),
    cadence: {
      version: state.version,
      startedAt: state.startedAt,
      paused: state.paused,
      pausedAt: state.pausedAt,
      completedKeys: normalizeCompletedKeys(state.completedKeys),
    },
  };
}

export function buildCadenceLeadUpdate(
  lead: LeadLike,
  startedAt: Date = getLeadCreatedAt(lead),
) {
  const state = buildInitialCadenceState(startedAt);
  const due = getNextCadenceDueDate(state, startedAt);

  return {
    metadata: mergeCadenceIntoMetadata(lead.metadata, state),
    due: due ? due.toISOString() : null,
  };
}

export function buildCadenceAdvanceUpdate(
  lead: LeadLike,
  state: LeadCadenceState,
  now: Date = new Date(),
) {
  const due = state.paused ? null : getNextCadenceDueDate(state, now);

  return {
    metadata: mergeCadenceIntoMetadata(lead.metadata, state),
    due: due ? due.toISOString() : null,
  };
}

export function getCadenceBoardSummary(
  lead: LeadLike,
  now: Date = new Date(),
): CadenceBoardSummary | null {
  if (!isCadenceEligible(lead)) {
    return null;
  }

  const state = resolveLeadCadenceState(lead);
  if (!state || state.paused) {
    return null;
  }

  const dueTouchpoints = getDueTouchpoints(state, now);
  const next = getNextIncompleteTouchpoint(state);
  if (!next) {
    return {
      label: 'Cadence complete',
      overdue: false,
      dueCount: 0,
      dayNumber: getCadenceDayNumber(state, now),
    };
  }

  const startedAt = new Date(state.startedAt);
  const dueAt = getTouchpointDueDate(startedAt, next.day, next.slot);
  const overdue = isBefore(dueAt, now) && dueTouchpoints.length > 0;

  if (dueTouchpoints.length > 1) {
    return {
      label: `Day ${next.day} · ${dueTouchpoints.length} due`,
      overdue,
      dueCount: dueTouchpoints.length,
      dayNumber: getCadenceDayNumber(state, now),
    };
  }

  return {
    label: formatTouchpointTitle(next),
    overdue,
    dueCount: dueTouchpoints.length,
    dayNumber: getCadenceDayNumber(state, now),
  };
}

export function isCadenceOverdue(lead: LeadLike, now: Date = new Date()) {
  if (!isCadenceEligible(lead)) {
    return false;
  }

  const persisted = resolveLeadCadenceState(lead);
  if (!persisted || persisted.paused) {
    return false;
  }

  const state = persisted;
  const dueTouchpoints = getDueTouchpoints(state, now);
  if (dueTouchpoints.length === 0) {
    return false;
  }

  const next = dueTouchpoints[0];
  const dueAt = getTouchpointDueDate(
    new Date(state.startedAt),
    next.day,
    next.slot,
  );
  return isBefore(dueAt, now);
}

export function getUpcomingTouchpoints(
  state: LeadCadenceState,
  limit = 5,
  now: Date = new Date(),
): CadenceTouchpoint[] {
  const startedAt = new Date(state.startedAt);

  return listCadenceTouchpoints()
    .filter((touchpoint) => {
      if (isTouchpointKeyCompleted(state, touchpoint.key)) {
        return false;
      }

      const dueAt = getTouchpointDueDate(
        startedAt,
        touchpoint.day,
        touchpoint.slot,
      );
      return isBefore(now, dueAt);
    })
    .slice(0, limit);
}

export function formatTouchpointTitle(
  touchpoint: Pick<CadenceTouchpoint, 'day' | 'slot' | 'channel'>,
) {
  return `Day ${touchpoint.day} · ${getCadenceSlotLabel(touchpoint.slot)} · ${getTouchpointLabel(touchpoint.channel)}`;
}

type LeadForCadenceTaskLabel = LeadLike & {
  content?: {
    name?: string | null;
    surname?: string | null;
  } | null;
};

export function getLeadCadenceTaskLabel(
  lead: LeadForCadenceTaskLabel,
  now: Date = new Date(),
) {
  const state = resolveLeadCadenceState(lead);
  const name = [lead.content?.name, lead.content?.surname]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (!state) {
    return name ? `${name} · follow-up due` : 'Lead follow-up';
  }

  if (state.paused) {
    return name ? `${name} · cadence paused` : 'Cadence paused';
  }

  const due = getDueTouchpoints(state, now);

  if (due.length === 0) {
    return name ? `${name} · follow-up due` : 'Lead follow-up';
  }

  const steps = due
    .map((touchpoint) => formatTouchpointTitle(touchpoint))
    .join(', ');

  return name ? `${name} · ${steps}` : steps;
}

export function buildCadenceTaskSummary(
  leads: LeadForCadenceTaskLabel[],
  now: Date = new Date(),
) {
  const lines = leads
    .map((lead) => `- ${getLeadCadenceTaskLabel(lead, now)}`)
    .filter(Boolean);

  if (lines.length === 0) {
    return '';
  }

  return `\n\nCadence steps due (see each lead for Day · slot · channel):\n${lines.join('\n')}`;
}

export function parseTouchpointKey(key: string) {
  const normalized = normalizeTouchpointKey(key);
  const parts = normalized.split(':');

  if (parts.length !== 3) {
    return null;
  }

  const [dayRaw, slot, channel] = parts;
  const day = Number(dayRaw);

  if (!day || (slot !== 'am' && slot !== 'pm') || !channel) {
    return null;
  }

  return {
    day,
    slot: slot as CadenceSlot,
    channel: channel as TouchpointChannel,
  };
}

export function getCadenceOverdueDays(
  state: LeadCadenceState,
  now: Date = new Date(),
) {
  const dueTouchpoints = getDueTouchpoints(state, now);
  if (dueTouchpoints.length === 0) {
    return 0;
  }

  const oldest = dueTouchpoints[0];
  const dueAt = getTouchpointDueDate(
    new Date(state.startedAt),
    oldest.day,
    oldest.slot,
  );
  return Math.max(
    0,
    differenceInCalendarDays(startOfDay(now), startOfDay(dueAt)),
  );
}

export function getFollowUpCadenceTier(
  lead: LeadLike,
  column?: string | null,
  now: Date = new Date(),
): FollowUpCadenceTier | null {
  if (column !== 'NEEDS_FOLLOWUP' || !isCadenceEligible(lead)) {
    return null;
  }

  const state = resolveLeadCadenceState(lead);
  if (!state || state.paused) {
    return null;
  }

  const dayNumber = getCadenceDayNumber(state, now);
  const overdueDays = getCadenceOverdueDays(state, now);

  if (overdueDays >= 8 || dayNumber >= 61) {
    return 'critical';
  }

  if (overdueDays >= 4 || dayNumber >= 31) {
    return 'stale';
  }

  if (overdueDays >= 1 || dayNumber >= 15) {
    return 'warming';
  }

  return 'fresh';
}

export function getFollowUpCadenceCardClass(
  tier: FollowUpCadenceTier | null,
  isDragging: boolean,
) {
  if (!tier) {
    return null;
  }

  const base =
    'py-4 px-2 flex flex-col justify-center transition-colors duration-100 hover:shadow-sm rounded border-2';

  if (isDragging) {
    return `${base} shadow-sm bg-orange-200 border-orange-400 text-orange-800`;
  }

  switch (tier) {
    case 'critical':
      return `${base} border-red-500 bg-red-100 hover:bg-red-200 text-red-700`;
    case 'stale':
      return `${base} border-orange-500 bg-orange-100 hover:bg-orange-200 text-orange-800`;
    case 'warming':
      return `${base} border-amber-400 bg-amber-100 hover:bg-amber-200 text-amber-900`;
    case 'fresh':
    default:
      return `${base} border-orange-300 bg-orange-50 hover:bg-orange-100 text-orange-800`;
  }
}

export const CADENCE_STAGE_FILTER_OPTIONS = [
  { value: 'all', label: 'All cadence stages' },
  { value: 'due', label: 'Any due work' },
  { value: 'reminder', label: 'Reminder due today' },
  ...LEAD_CADENCE_SCHEDULE.map((step) => ({
    value: String(step.day),
    label: `Day ${step.day}`,
  })),
] as const;

export function hasLeadReminderSet(lead: LeadLike) {
  return Boolean(lead.due);
}

export function isLeadReminderDueToday(lead: LeadLike, now: Date = new Date()) {
  if (!lead.due) {
    return false;
  }

  return isSameDay(new Date(lead.due), now);
}

export function matchesCadenceStageFilter(
  lead: LeadLike,
  filter: string,
  now: Date = new Date(),
): boolean {
  if (!filter || filter === 'all') {
    return true;
  }

  if (filter === 'reminder') {
    return isLeadReminderDueToday(lead, now);
  }

  if (!isCadenceEligible(lead)) {
    return false;
  }

  const state = resolveLeadCadenceState(lead);
  if (!state || state.paused) {
    return false;
  }

  if (filter === 'due') {
    return isLeadCadenceWorkDue(lead, now);
  }

  const targetDay = Number(filter);
  if (!Number.isFinite(targetDay)) {
    return true;
  }

  const dueTouchpoints = getDueTouchpoints(state, now);
  if (dueTouchpoints.some((touchpoint) => touchpoint.day === targetDay)) {
    return true;
  }

  const next = getNextIncompleteTouchpoint(state);
  return next?.day === targetDay;
}

export function getSkipReasonLabel(value?: string | null) {
  if (!value) {
    return null;
  }

  return (
    CADENCE_SKIP_REASONS.find((reason) => reason.value === value)?.label ||
    value
  );
}
